import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import SportsSoccerRoundedIcon from '@mui/icons-material/SportsSoccerRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import AppLayout from '../../components/ui/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import SectionCard from '../../components/ui/SectionCard';
import StatCard from '../../components/ui/StatCard';
import LoadingState from '../../components/ui/LoadingState';
import EmptyState from '../../components/ui/EmptyState';
import RankingList from '../../components/ui/RankingList';
import ParceirosCard from '../../components/ParceirosCard';
import PlayerAvatar from '../../components/ui/PlayerAvatar';
import BarChart from '../../charts/bar';
import GuestToggle from '../../components/GuestToggle';
import RankingGoleiros from '../../components/RankingGoleiros';
import PeladaLockButton from '../../components/PeladaLockButton';
import Snackbar from '@mui/material/Snackbar';
import { cardColor } from '../jogos/GameCard';
import api from '../../api';
import tokens from '../../theme/tokens';
import BolaAnteriorAlerta from '../../components/BolaAnteriorAlerta';

// Painel geral da pelada para qualquer jogador logado.
const PeladaPanel = () => {
  const navigate = useNavigate();
  const [ano, setAno] = useState(''); // vazio: a API escolhe a temporada mais recente com peladas
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [peladaAberta, setPeladaAberta] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setCarregando(true);
    api
      .get('pelada/estatisticas', { params: ano ? { ano } : {} })
      .then((response) => setDados(response.data))
      .catch(() => setDados(null))
      .finally(() => setCarregando(false));
  }, [ano]);

  const carregarPeladaAberta = () => api.get('getPeladaActive').then((r) => setPeladaAberta(r.data.data || null)).catch(() => {});

  useEffect(() => {
    carregarPeladaAberta();
  }, []);

  const crack = dados && dados.cracks[0];

  return (
    <AppLayout>
      <PageHeader
        eyebrow="Pelada Riviera"
        title="Painel da pelada"
        subtitle="Números gerais e rankings da temporada. Seus números ficam em Meu perfil."
        actions={
          <>
            <PeladaLockButton
              pelada={peladaAberta}
              onChange={carregarPeladaAberta}
              onMessage={(message, severity = 'success') => setToast({ message, severity })}
            />
            {dados?.anosDisponiveis?.length > 0 && (
              <TextField select size="small" label="Temporada" value={dados.ano} onChange={(e) => setAno(e.target.value)} sx={{ minWidth: 140 }}>
                {dados.anosDisponiveis.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </TextField>
            )}
          </>
        }
      />

      <BolaAnteriorAlerta />

      {peladaAberta && (
        <Alert severity="info" icon={<PlaceRoundedIcon />} sx={{ mb: 2, alignItems: 'center' }} action={<Button variant="contained" size="small" onClick={() => navigate('/ordem-chegada')}>Confirmação / Ordem de chegada</Button>}>
          Tem pelada aberta! Registre sua chegada quando estiver na concentração.
        </Alert>
      )}
      {peladaAberta && (
        <GuestToggle
          pelada={peladaAberta}
          onChange={(atualizada) => setPeladaAberta((atual) => ({ ...atual, openGuest: atualizada.openGuest }))}
          onMessage={(message, severity = 'success') => setToast({ message, severity })}
          sx={{ mb: 2 }}
        />
      )}
      {dados?.ultimaAvaliacao && (
        <Alert
          severity={dados.ultimaAvaliacao.status === 'aberta' ? 'warning' : 'success'}
          icon={<StarRoundedIcon />}
          sx={{ mb: 3, alignItems: 'center' }}
          action={<Button variant="contained" size="small" onClick={() => navigate(`/avaliacao/${dados.ultimaAvaliacao.peladaId}`)}>{dados.ultimaAvaliacao.status === 'aberta' ? 'Avaliar' : 'Ver resultado'}</Button>}
        >
          {dados.ultimaAvaliacao.status === 'aberta' ? 'A avaliação da última pelada está aberta. Dê estrelas para quem jogou com você!' : 'Saiu o resultado da avaliação da última pelada.'}
        </Alert>
      )}

      {carregando ? (
        <LoadingState label="Carregando estatísticas..." />
      ) : !dados ? (
        <Card><EmptyState title="Não foi possível carregar as estatísticas" /></Card>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}><StatCard label="Peladas no ano" value={dados.geral.peladas} hint={`${dados.geral.partidas} partidas`} icon={<EventAvailableRoundedIcon />} tone="brand" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Média de jogadores" value={dados.geral.mediaJogadores} hint="por pelada" icon={<GroupsRoundedIcon />} tone="info" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Gols registrados" value={dados.geral.gols} icon={<SportsSoccerRoundedIcon />} tone="success" /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Jogadores na temporada" value={dados.geral.jogadoresDiferentes} hint="jogaram pelo menos uma pelada" icon={<EmojiEventsRoundedIcon />} tone="gold" /></Grid>
          </Grid>

          {/* Em destaque: com quem você mais rende no mesmo time na temporada escolhida */}
          <ParceirosCard ano={dados.ano} sx={{ mb: 3 }} />

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <SectionCard title="Jogadores por pelada" subtitle="Check-ins nas últimas peladas" sx={{ height: '100%' }}>
                {dados.jogadoresPorPelada.length === 0 ? (
                  <EmptyState title="Nenhuma pelada na temporada" />
                ) : (
                  <BarChart
                    labels={dados.jogadoresPorPelada.map((p) => p.label)}
                    series={[
                      { label: 'Jogadores', data: dados.jogadoresPorPelada.map((p) => p.checkins - p.convidados), color: tokens.color.brand },
                      { label: 'Convidados', data: dados.jogadoresPorPelada.map((p) => p.convidados), color: tokens.color.gold },
                    ]}
                    stacked
                  />
                )}
              </SectionCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', p: 3, color: '#fff', textAlign: 'center', border: 'none', background: `radial-gradient(circle at 50% 0%, rgba(233,168,0,0.35), transparent 60%), linear-gradient(160deg, ${tokens.color.inkSoft}, ${tokens.color.ink})` }}>
                <StarRoundedIcon sx={{ color: tokens.color.gold, fontSize: 36 }} />
                <Typography variant="overline" sx={{ display: 'block', opacity: 0.8 }}>Crack da pelada {dados.ano}</Typography>
                {crack ? (
                  <>
                    <PlayerAvatar src={crack.foto} name={crack.nome} size={110} sx={{ mx: 'auto', my: 2, border: `4px solid ${tokens.color.gold}` }} />
                    <Typography variant="h5">{crack.nome}</Typography>
                    <Typography sx={{ opacity: 0.8 }}>Eleito {crack.vezes}x nas avaliações</Typography>
                  </>
                ) : (
                  <Typography sx={{ opacity: 0.75, mt: 3 }}>Ainda não houve avaliações encerradas nesta temporada.</Typography>
                )}
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6} lg={4}>
              <SectionCard title="Mais assíduos" subtitle="Check-ins na temporada" sx={{ height: '100%' }}>
                <RankingList items={dados.assiduos} valueKey="presencas" empty="Sem presenças registradas" />
              </SectionCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <SectionCard title="Artilharia da pelada" subtitle="Gols lançados pelo árbitro" sx={{ height: '100%' }}>
                <RankingList items={dados.artilharia} valueKey="gols" color={tokens.color.brand} empty="Nenhum gol registrado ainda" />
              </SectionCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <SectionCard title="Crack da pelada" subtitle="Mais vezes eleito nas avaliações" sx={{ height: '100%' }}>
                <RankingList items={dados.cracks} valueKey="vezes" suffix="x" color={tokens.color.gold} empty="Nenhuma avaliação encerrada" />
              </SectionCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <SectionCard
                title="Ranking das avaliações"
                subtitle={`Média das estrelas recebidas nas peladas · mínimo de ${dados.rankingAvaliacoes?.minimoVotos || 1} notas`}
                sx={{ height: '100%' }}
              >
                <RankingList
                  items={(dados.rankingAvaliacoes?.lista || []).map((a) => ({ ...a, mediaTexto: a.media.toFixed(2).replace('.', ',') }))}
                  valueKey="mediaTexto"
                  suffix="★"
                  color={tokens.color.gold}
                  empty="Nenhuma avaliação na temporada"
                />
                {dados.eu?.avaliacao && (
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
                    {dados.eu.avaliacao.posicao
                      ? `Você: ${dados.eu.avaliacao.posicao}º · média ${dados.eu.avaliacao.media.toFixed(2).replace('.', ',')}★ em ${dados.eu.avaliacao.votos} notas`
                      : `Você: média ${dados.eu.avaliacao.media.toFixed(2).replace('.', ',')}★ em ${dados.eu.avaliacao.votos} notas (abaixo do mínimo para o ranking)`}
                  </Typography>
                )}
              </SectionCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <SectionCard title="Cartões amarelos" icon={<Box sx={{ width: 14, height: 18, borderRadius: 0.5, bgcolor: cardColor.amarelo }} />} sx={{ height: '100%' }}>
                <RankingList items={dados.cartoesAmarelos} valueKey="amarelos" empty="Nenhum cartão amarelo" />
              </SectionCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <SectionCard title="Cartões azuis" icon={<Box sx={{ width: 14, height: 18, borderRadius: 0.5, bgcolor: cardColor.azul }} />} sx={{ height: '100%' }}>
                <RankingList items={dados.cartoesAzuis} valueKey="azuis" empty="Nenhum cartão azul" />
              </SectionCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <SectionCard title="Mais vitórias" subtitle="Partidas vencidas na temporada" sx={{ height: '100%' }}>
                <RankingList items={dados.maisVitorias} valueKey="vitorias" color={tokens.color.success} empty="Sem partidas finalizadas" />
              </SectionCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <RankingGoleiros goleiros={dados.goleiros || []} sx={{ height: '100%' }} />
            </Grid>
          </Grid>
        </>
      )}
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert variant="filled" severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : <span />}
      </Snackbar>
    </AppLayout>
  );
};

export default PeladaPanel;
